import { fabric } from 'goformz-fabric';
import './TriangleFix';
import { getRgba } from '../helpers/helpers';
import { debounce } from '../helpers/utility';

export const enum SketchMode {
  NONE = 0,
  FREEDRAW = 1,
  HIGHLIGHT = 2,
  FILL = 3,
  RECTANGLE = 4,
  CIRCLE = 5,
  LINE = 6,
  ARROW = 7,
  TEXT = 8,
  TRIANGLE = 9,
  MOVEBEHIND = 10,
  MOVEFRONT = 11
}

export type AvailableOptionTypes = 'textSelected' | 'undoAvailable' | 'redoAvailable' | 'containsImage' | 'objectSelected';

export default class Sketch {
  private canvas: TypeMePlease;
  private originalWidth: number;
  private width: number;
  private height: number;
  private align: any;
  private onAvailableOptionsChanged: (name: AvailableOptionTypes, value: boolean) => void;
  private showPrompt: AnyFunc;
  private editable: boolean;
  private arrowWidthFactor: number;
  private curX: number;
  private curY: number;
  private curLine: any;
  private fileId: string;
  private lastAdded: TypeMePlease;
  private fabricCanvas: TypeMePlease;
  private editingText: boolean;
  private sketchTarget: TypeMePlease;
  private sketchMode: SketchMode;
  private imageAndFileIds: Record<string, string>;
  private objectScale: TypeMePlease;
  private selectedColor: string;
  private strokeOpacity: number;
  private currentMode: SketchMode;
  private strokeColor: string;
  private undoStack: string[];
  private currentState: string;
  private redoStack: string[];
  private undoStackLength: number;
  private ignoreChangesForUndo: boolean;
  private ignoreSelectionEvents: boolean;
  private lastAvailableOptions: object;

  constructor(canvas, width, height, align, onAvailableOptionsChanged: (name: AvailableOptionTypes, value: boolean) => void, showPrompt) {
    this.getValue = this.getValue.bind(this);
    this.setValue = this.setValue.bind(this);
    this.setBackgroundImage = this.setBackgroundImage.bind(this);
    this.clearBackgroundImage = this.clearBackgroundImage.bind(this);
    this.openTextPromptForCurrentSelection = this.openTextPromptForCurrentSelection.bind(this);
    this.openTextPrompt = this.openTextPrompt.bind(this);
    this.setTextValue = this.setTextValue.bind(this);
    this.resize = this.resize.bind(this);
    this.getDataImage = this.getDataImage.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onObjectScaling = this.onObjectScaling.bind(this);
    this.onObjectMoving = this.onObjectMoving.bind(this);
    this.onDeselect = this.onDeselect.bind(this);
    this.onDeleteObject = this.onDeleteObject.bind(this);
    this.onDeleteAllObjects = this.onDeleteAllObjects.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.onSelectUpdated = this.onSelectUpdated.bind(this);
    this.addHandlebars = this.addHandlebars.bind(this);
    this.removeHandlebars = this.removeHandlebars.bind(this);
    this.removeAllHandlebars = this.removeAllHandlebars.bind(this);
    this.removeBackgroundImage = this.removeBackgroundImage.bind(this);
    this.transformLegacyValue = this.transformLegacyValue.bind(this);
    this.bringForward = this.bringForward.bind(this);
    this.sendBackwards = this.sendBackwards.bind(this);
    this.keepBackgroundInBack = this.keepBackgroundInBack.bind(this);
    this.setCanvasPropAll = this.setCanvasPropAll.bind(this);
    this.addCurrentStateToUndoStack = debounce(this.addCurrentStateToUndoStack.bind(this), 200);
    this.onFabricChanged = this.onFabricChanged.bind(this);
    this.undo = this.undo.bind(this);
    this.redo = this.redo.bind(this);
    this.getAvailableOptions = this.getAvailableOptions.bind(this);
    this.checkAvailableOptionChanges = this.checkAvailableOptionChanges.bind(this);
    this.isBackgroundImageObject = this.isBackgroundImageObject.bind(this);
    this.restoreObject = this.restoreObject.bind(this);
    this.getEventClientX = this.getEventClientX.bind(this);
    this.getEventClientY = this.getEventClientY.bind(this);
    this.getRootActiveObject = this.getRootActiveObject.bind(this);
    this.restoreToState = this.restoreToState.bind(this);
    this.scaleObject = this.scaleObject.bind(this);

    this.canvas = canvas;
    this.originalWidth = width;
    this.width = width;
    this.height = height;
    this.align = align;
    this.onAvailableOptionsChanged = onAvailableOptionsChanged || function () {};
    this.showPrompt = showPrompt || function () {};
    this.editable = true;
    this.arrowWidthFactor = 2.5;
    //to save the old cursor position: used on line moving
    this.curX = null;
    this.curY = null;

    this.curLine = null;
    this.fileId = null;
    this.lastAdded = null;

    this.undoStack = [];
    this.currentState = null;
    this.redoStack = [];
    this.undoStackLength = 20;
    this.imageAndFileIds = {};

    this.fabricCanvas = new fabric.Canvas(canvas);
    this.fabricCanvas.freeDrawingBrush = new fabric.PencilBrush(this.fabricCanvas);
    this.fabricCanvas.canvasScale = 1;
    this.fabricCanvas.preserveObjectStacking = true;
    this.setMode(SketchMode.FREEDRAW);
    this.setStrokeColor('#000000');
    this.setObjectScale(4);

    this.fabricCanvas.on('mouse:down', this.onMouseDown);
    this.fabricCanvas.on('mouse:up', this.onMouseUp);
    this.fabricCanvas.on('mouse:move', this.onMouseMove);
    this.fabricCanvas.on('object:scaling', this.onObjectScaling);
    this.fabricCanvas.on('object:moving', this.onObjectMoving);
    this.fabricCanvas.on('selection:cleared', this.onDeselect);
    this.fabricCanvas.on('selection:created', this.onSelect);
    this.fabricCanvas.on('selection:updated', this.onSelectUpdated);

    this.fabricCanvas.on('object:modified', this.onFabricChanged);
    this.fabricCanvas.on('object:moving', this.onFabricChanged);
    this.fabricCanvas.on('object:scaling', this.onFabricChanged);
    this.fabricCanvas.on('object:rotating', this.onFabricChanged);
    this.fabricCanvas.on('object:skewing', this.onFabricChanged);
    this.fabricCanvas.on('object:resizing', this.onFabricChanged);
    this.fabricCanvas.on('object:added', this.onFabricChanged);
    this.fabricCanvas.on('object:removed', this.onFabricChanged);
    this.fabricCanvas.on('path:created', this.onFabricChanged);
    this.fabricCanvas.on('text:changed', this.onFabricChanged);
  }

  setCanvasPropAll(propName, value) {
    const canvas = this.fabricCanvas;
    const objects = canvas.getObjects();
    for (const i in objects) {
      if (objects.hasOwnProperty(i) && !this.isBackgroundImageObject(objects[i]) && !objects[i].discardOnSave) {
        switch (propName) {
          case 'evented':
            objects[i].evented = value;
            break;
          case 'perPixelTargetFind':
            objects[i].perPixelTargetFind = value;
            break;
          case 'selectable':
            objects[i].selectable = value;
            break;
          default:
            break;
        }
      }
    }
    canvas.renderAll();
  }

  isBackgroundImageObject(obj) {
    return obj.type === 'image';
  }

  resize(newWidth) {
    const canvas = this.fabricCanvas;
    if (canvas.width !== newWidth) {
      const scaleMultiplier = newWidth / canvas.width;
      const objects = canvas.getObjects();
      this.width = newWidth;
      for (const i in objects) {
        if (objects.hasOwnProperty(i)) {
          this.scaleObject(objects[i], scaleMultiplier);
          objects[i].setCoords();
        }
      }
      canvas.discardActiveObject();
      canvas.setWidth(canvas.getWidth() * scaleMultiplier);
      canvas.setHeight(canvas.getHeight() * scaleMultiplier);
      canvas.renderAll();
      canvas.calcOffset();

      // resize each item in undo/redo stack
      if (this.currentState) {
        const objects = JSON.parse(this.currentState);
        objects.forEach((obj) => {
          this.scaleObject(obj, scaleMultiplier);
        });
        this.currentState = JSON.stringify(objects);
      }

      for (let i = 0; i < this.undoStack.length; i++) {
        const objects = JSON.parse(this.undoStack[i]);
        objects.forEach((obj) => {
          this.scaleObject(obj, scaleMultiplier);
        });
        this.undoStack[i] = JSON.stringify(objects);
      }

      for (let i = 0; i < this.redoStack.length; i++) {
        const objects = JSON.parse(this.redoStack[i]);
        objects.forEach((obj) => {
          this.scaleObject(obj, scaleMultiplier);
        });
        this.redoStack[i] = JSON.stringify(objects);
      }
    }
  }

  scaleObject(obj, scaleMultiplier) {
    obj.scaleX *= scaleMultiplier;
    obj.scaleY *= scaleMultiplier;
    obj.left *= scaleMultiplier;
    obj.top *= scaleMultiplier;
    obj.fullScale = obj.scaleX * (1000 / this.width);
    if (obj.type === 'line') {
      obj.originalX1 *= scaleMultiplier;
      obj.originalY1 *= scaleMultiplier;
      obj.originalX2 *= scaleMultiplier;
      obj.originalY2 *= scaleMultiplier;
    }
  }

  openTextPromptForCurrentSelection() {
    const obj = this.fabricCanvas.getActiveObject();
    this.openTextPrompt(obj.text, obj, obj.left, obj.top);
  }

  openTextPrompt(textObject, target, x, y) {
    this.editingText = true;
    this.showPrompt(
      {
        title: 'Add Text',
        msg: '',
        text: textObject,
        maxLength: 200,
        multiLine: true
      },
      (result, text) => {
        this.editingText = false;
        if (result) {
          this.setTextValue(text, target, x, y);
          this.addCurrentStateToUndoStack();
        }
        this.fabricCanvas.renderAll();
      }
    );
  }

  setTextValue(text, target, x, y) {
    if (!target) {
      this.sketchTarget = new fabric.Text(text, {
        left: x,
        top: y,
        originX: 'left',
        originY: 'center',
        stroke: this.fabricCanvas.freeDrawingBrush.color || 'black',
        fill: this.fabricCanvas.freeDrawingBrush.color || 'black',
        fontFamily: 'Helvetica',
        fontSize: 10 * (this.fabricCanvas.freeDrawingBrush.width || 1)
      });

      this.fabricCanvas.add(this.sketchTarget);
      this.fabricCanvas.renderAll();
      this.lastAdded = this.sketchTarget;
      delete this.sketchTarget;
    } else {
      target.set('text', text);
    }
    this.addCurrentStateToUndoStack();
  }

  getDataImage(newWidth) {
    const saveCanvas = new fabric.Canvas('saveCanvas');
    saveCanvas.loadFromJSON(JSON.stringify(this.fabricCanvas));

    saveCanvas.setWidth(this.fabricCanvas.getWidth());
    saveCanvas.setHeight(this.fabricCanvas.getHeight());
    saveCanvas.renderAll();

    if (saveCanvas.width !== newWidth) {
      const scaleMultiplier = newWidth / saveCanvas.width;
      const objects = saveCanvas.getObjects();
      for (const i in objects) {
        if (objects.hasOwnProperty(i)) {
          objects[i].scaleX *= scaleMultiplier;
          objects[i].scaleY *= scaleMultiplier;
          objects[i].left *= scaleMultiplier;
          objects[i].top *= scaleMultiplier;
          objects[i].fullScale = objects[i].scaleX * (1000 / this.width);
          if (objects[i].type === 'line') {
            objects[i].originalX1 *= scaleMultiplier;
            objects[i].originalY1 *= scaleMultiplier;
            objects[i].originalX2 *= scaleMultiplier;
            objects[i].originalY2 *= scaleMultiplier;
          }
          objects[i].setCoords();
        }
      }
      // const obj = canvas.backgroundImage;
      // if(obj){
      //     obj.scaleX = obj.scaleX * scaleMultiplier;
      //     obj.scaleY = obj.scaleY * scaleMultiplier;
      // }

      saveCanvas.discardActiveObject();
      saveCanvas.setWidth(saveCanvas.getWidth() * scaleMultiplier);
      saveCanvas.setHeight(saveCanvas.getHeight() * scaleMultiplier);
      saveCanvas.renderAll();
      saveCanvas.calcOffset();
    }
    return saveCanvas.toDataURL();
  }

  getEventClientX(e) {
    return e.clientX || (e.touches && e.touches.length > 0 ? e.touches[0].clientX : undefined);
  }

  getEventClientY(e) {
    return e.clientY || (e.touches && e.touches.length > 0 ? e.touches[0].clientY : undefined);
  }

  onMouseDown(options) {
    if (!this.editable) {
      return;
    }

    this.ignoreChangesForUndo = true;

    options = options || {};

    const target = this.fabricCanvas.findTarget(options.e);
    const p = this.fabricCanvas.getPointer(options.e);

    if (target && this.sketchMode !== SketchMode.FILL) {
      this.curX = this.getEventClientX(options.e);
      this.curY = this.getEventClientY(options.e);
    } else if (target && this.sketchMode === SketchMode.FILL) {
      target.set(target.type === 'path' ? 'stroke' : 'fill', this.fabricCanvas.freeDrawingBrush.color || 'black');
      this.fabricCanvas.discardActiveObject();
    } else if (this.sketchMode === SketchMode.RECTANGLE) {
      this.sketchTarget = new fabric.Rect({
        width: 10,
        height: 10,
        fill: false
      });
      this.sketchTarget.originalTop = p.y;
      this.sketchTarget.originalLeft = p.x;
      this.sketchTarget.perPixelTargetFind = true;
    } else if (this.sketchMode === SketchMode.TRIANGLE) {
      this.sketchTarget = new fabric.Triangle({
        width: 10,
        height: 10,
        fill: false
      });
      this.sketchTarget.originalTop = p.y;
      this.sketchTarget.originalLeft = p.x;
      this.sketchTarget.perPixelTargetFind = true;
    } else if (this.sketchMode === SketchMode.LINE || this.sketchMode === SketchMode.ARROW) {
      this.sketchTarget = new fabric.Line([p.x, p.y, p.x + 1, p.y + 1], {
        type: 'line',
        fill: this.fabricCanvas.freeDrawingBrush.color || 'black',
        stroke: this.fabricCanvas.freeDrawingBrush.color || 'black',
        strokeWidth: this.fabricCanvas.freeDrawingBrush.width || 1,
        originX: 'center',
        originY: 'center',
        selectable: true,
        arrowStyle: this.sketchMode === SketchMode.ARROW ? 1 : 0 //TODO someday support different arrow styles
      });

      this.sketchTarget.handleBars = {
        grab1: new fabric.Circle({
          left: p.x,
          top: p.y,
          strokeWidth: 5,
          radius: 7,
          fill: '#99CCFF',
          stroke: '#99CCFF',
          originX: 'center',
          originY: 'center',
          opacity: 0.5,
          grab1: true
        }),
        grab2: new fabric.Circle({
          left: p.x,
          top: p.y,
          strokeWidth: 5,
          radius: 7,
          fill: '#99CCFF',
          stroke: '#99CCFF',
          originX: 'center',
          originY: 'center',
          opacity: 0.5,
          grab2: true
        })
      };
      this.sketchTarget.originalX1 = p.x;
      this.sketchTarget.originalY1 = p.y;
      this.sketchTarget.hasControls = false;
      this.sketchTarget.hasBorders = false;
      this.sketchTarget.perPixelTargetFind = true;
      this.sketchTarget.handleBars.grab1.hasControls = false;
      this.sketchTarget.handleBars.grab1.hasBorders = false;
      this.sketchTarget.handleBars.grab2.hasControls = false;
      this.sketchTarget.handleBars.grab2.hasBorders = false;
      this.sketchTarget.handleBars.grab1.parent = this.sketchTarget;
      this.sketchTarget.handleBars.grab2.parent = this.sketchTarget;

      if (this.curLine !== this.sketchTarget && this.curLine && this.curLine.handleBars) {
        this.removeHandlebars(this.curLine);
      }
      this.curLine = this.sketchTarget;
    } else if (this.sketchMode === SketchMode.CIRCLE) {
      this.sketchTarget = new fabric.Ellipse({
        rx: 10,
        ry: 10,
        fill: false
      });
      this.sketchTarget.originalTop = p.y;
      this.sketchTarget.originalLeft = p.x;
      this.sketchTarget.perPixelTargetFind = true;
    } else if (this.sketchMode === SketchMode.TEXT) {
      this.fabricCanvas.discardActiveObject();
      const currentText = target && target.type === 'text' ? target.get('text') : '';
      if (target && target.type === 'text') {
        this.openTextPrompt(currentText, target, p.x, p.y);
      } else {
        this.openTextPrompt('', null, p.x, p.y);
      }
    }

    if (!target && this.curLine !== this.sketchTarget && this.curLine && this.curLine.handleBars) {
      this.removeHandlebars(this.curLine);
    }

    if (this.sketchTarget && this.sketchTarget.type !== 'line' && this.sketchTarget.type !== 'text') {
      this.sketchTarget.set('top', p.y);
      this.sketchTarget.set('left', p.x);
      this.sketchTarget.set('originX', 'left');
      this.sketchTarget.set('originY', 'top');
      this.sketchTarget.set('stroke', this.fabricCanvas.freeDrawingBrush.color || 'black');
      this.sketchTarget.set('strokeWidth', this.fabricCanvas.freeDrawingBrush.width || 1);
    }

    this.lastAdded = this.sketchTarget;
  }

  onMouseMove(options) {
    if (!this.editable) {
      return;
    }

    const p = this.fabricCanvas.getPointer(options.e);
    if (this.sketchTarget) {
      // Don't add the sketchTarget until the drawing is at least 10px in width,
      // but always add Arrow and Line since their width doesn't change as you draw
      if (!this.sketchTarget.added && (Math.abs(this.sketchTarget.width) > 10 || this.sketchMode === SketchMode.ARROW || this.sketchMode === SketchMode.LINE)) {
        this.fabricCanvas.add(this.sketchTarget);
        this.sketchTarget.added = true;

        if (this.sketchMode === SketchMode.ARROW && !this.sketchTarget.arrowTriangle) {
          const arrowAngle = ((Math.atan2(p.x - this.sketchTarget.get('x1'), p.y - this.sketchTarget.get('y1')) * 180) / Math.PI - 180) * -1;
          this.sketchTarget.arrowTriangle = new fabric.Triangle({
            selectable: false,
            fill: this.fabricCanvas.freeDrawingBrush.color || 'black',
            stroke: this.fabricCanvas.freeDrawingBrush.color || 'black',
            angle: arrowAngle,
            left: p.x,
            top: p.y,
            width: this.sketchTarget.strokeWidth * this.arrowWidthFactor,
            height: this.sketchTarget.strokeWidth * 2.2,
            originX: 'center',
            originY: 'center',
            discardOnSave: true
          });
          this.fabricCanvas.add(this.sketchTarget.arrowTriangle);
        }

        //add the handlebars if line
        if ((this.sketchMode === SketchMode.LINE || this.sketchMode === SketchMode.ARROW) && this.sketchTarget.handleBars) {
          this.fabricCanvas.add(this.sketchTarget.handleBars.grab1);
          this.fabricCanvas.add(this.sketchTarget.handleBars.grab2);
        }
      }

      if (this.sketchMode === SketchMode.RECTANGLE || this.sketchMode === SketchMode.TRIANGLE) {
        if (this.sketchTarget.originalLeft > p.x) {
          this.sketchTarget.set('left', p.x);
        }
        if (this.sketchTarget.originalTop > p.y) {
          this.sketchTarget.set('top', p.y);
        }
        this.sketchTarget.set('width', Math.abs(p.x - this.sketchTarget.originalLeft));
        this.sketchTarget.set('height', Math.abs(p.y - this.sketchTarget.originalTop));
      } else if (this.sketchMode === SketchMode.LINE || this.sketchMode === SketchMode.ARROW) {
        //snap the lines to within 5 degrees of horizontal or vertical axis
        const snapTo = 5;
        const angle = (Math.atan2(p.x - this.sketchTarget.get('x1'), p.y - this.sketchTarget.get('y1')) * 180) / Math.PI;
        let arrowAngle = ((Math.atan2(p.x - this.sketchTarget.get('x1'), p.y - this.sketchTarget.get('y1')) * 180) / Math.PI - 180) * -1;
        let x2 = p.x;
        let y2 = p.y;
        this.sketchTarget.set({ x2: p.x, y2: p.y });
        if ((angle >= 90 - snapTo && angle <= 90 + snapTo) || (angle >= -90 - snapTo && angle <= -90 + snapTo)) {
          //pointed left or right
          x2 = p.x;
          y2 = this.sketchTarget.get('y1');
          this.sketchTarget.set({ x2, y2 });
          this.sketchTarget.setCoords();
          arrowAngle = angle >= 90 - snapTo && angle <= 90 + snapTo ? 90 : -90;
        } else if (angle >= 180 - snapTo || angle <= -180 + snapTo || (angle <= snapTo && angle >= -snapTo)) {
          //pointed up and down
          x2 = this.sketchTarget.get('x1');
          y2 = p.y;
          this.sketchTarget.set({ x2, y2 });
          this.sketchTarget.setCoords();
          arrowAngle = angle >= 180 - snapTo || angle <= -180 + snapTo ? 0 : 180;
        }

        //move the handles
        this.sketchTarget.handleBars.grab2.set('left', x2);
        this.sketchTarget.handleBars.grab2.set('top', y2);
        this.sketchTarget.originalX2 = x2;
        this.sketchTarget.originalY2 = y2;

        //Move the arrow
        if (this.sketchMode === SketchMode.ARROW && this.sketchTarget.arrowTriangle) {
          this.sketchTarget.arrowTriangle.set('left', x2);
          this.sketchTarget.arrowTriangle.set('top', y2);
          this.sketchTarget.arrowTriangle.set('angle', arrowAngle);
        }
      } else if (this.sketchMode === SketchMode.CIRCLE) {
        this.sketchTarget.set('rx', Math.abs(p.x - this.sketchTarget.originalLeft) / 2);
        this.sketchTarget.set('ry', Math.abs(p.y - this.sketchTarget.originalTop) / 2);

        if (this.sketchTarget.originalLeft > p.x) {
          this.sketchTarget.set('left', p.x);
        }
        if (this.sketchTarget.originalTop > p.y) {
          this.sketchTarget.set('top', p.y);
        }
        this.sketchTarget.set('width', Math.abs(p.x - this.sketchTarget.originalLeft));
        this.sketchTarget.set('height', Math.abs(p.y - this.sketchTarget.originalTop));
      }
      this.fabricCanvas.renderAll();
      this.onFabricChanged();
    }
  }

  onMouseUp(options) {
    if (this.fabricCanvas.getActiveObjects().length > 1) {
      const finalSelection = [];
      this.fabricCanvas.getActiveObjects().forEach((o) => {
        if (o.type !== 'line' && o.type !== 'circle') {
          finalSelection.push(o);
        }
      });
      this.fabricCanvas.discardActiveObject();
      const selection = new fabric.ActiveSelection(finalSelection, {
        canvas: this.fabricCanvas
      });

      this.fabricCanvas.setActiveObject(selection);
    }

    //reset last pointer position
    this.curX = null;
    this.curY = null;
    if (!this.editable) {
      return;
    }

    if (this.sketchTarget) {
      if (
        this.sketchTarget.type === 'ellipse' ||
        this.sketchTarget.type === 'rect' ||
        this.sketchTarget.type === 'triangle' ||
        this.sketchTarget.type === 'sketchline' ||
        this.sketchTarget.type === 'line'
      ) {
        //Need this since rehydrating the sketch will cause neg widths and heights for ellipses to be made positive which causes it to be drawn wrong
        if (this.sketchTarget.get('width') < 0) {
          this.sketchTarget.set('left', this.sketchTarget.get('left') + this.sketchTarget.get('width'));
          this.sketchTarget.set('width', this.sketchTarget.get('width') * -1);
          this.sketchTarget.set('flipX', true);
        }
        if (this.sketchTarget.get('height') < 0) {
          this.sketchTarget.set('top', this.sketchTarget.get('top') + this.sketchTarget.get('height'));
          this.sketchTarget.set('height', this.sketchTarget.get('height') * -1);
          //negative heights caused lines and triangles to flip, this fixes that
          this.sketchTarget.set('flipY', true);
        }
      }
      this.sketchTarget.setCoords();
      this.sketchTarget.evented = false;
      const t = this.fabricCanvas.findTarget(options.e);

      if (!this.sketchTarget.added && t) {
        // TODO?
        // if (t.groupable !== false) {
        //   this.fabricCanvas._handleGroupLogic(options.e, t);
        // }
        if (!this.fabricCanvas.getActiveObjects()) {
          this.fabricCanvas.setActiveObject(t);
        }
      } else {
        //this.fabricCanvas.discardActiveObject();
      }

      if (this.curLine) {
        this.removeHandlebars(this.curLine);
      }

      delete this.sketchTarget;
      this.fabricCanvas.renderAll();
    }

    if (options.target) {
      options.target.setCoords();
    }

    this.ignoreChangesForUndo = false;
    this.onFabricChanged();
  }

  onDeleteObject() {
    if (this.editingText) {
      return;
    }

    this.fabricCanvas.getActiveObjects().forEach((obj) => {
      if (obj.handleBars) {
        this.fabricCanvas.remove(obj.handleBars.grab1);
        this.fabricCanvas.remove(obj.handleBars.grab2);
      }
      if (obj.arrowTriangle) {
        this.fabricCanvas.remove(obj.arrowTriangle);
      }
      if (obj.type === 'circle') {
        if (obj.parent.arrowTriangle) {
          this.fabricCanvas.remove(obj.parent.arrowTriangle);
        }
        this.fabricCanvas.remove(obj.parent.handleBars.grab1);
        this.fabricCanvas.remove(obj.parent.handleBars.grab2);
        this.fabricCanvas.remove(obj.parent);
      }
      this.fabricCanvas.remove(obj);
    });
    this.fabricCanvas.discardActiveObject().renderAll();
  }

  onDeleteAllObjects() {
    const allObjects = this.fabricCanvas.getObjects();
    this.fabricCanvas.discardActiveObject();
    allObjects.forEach((obj) => {
      if (!this.isBackgroundImageObject(obj)) {
        this.fabricCanvas.remove(obj);
      }
    });
    this.fabricCanvas.renderAll();
  }

  onObjectScaling(options) {
    if (!this.editable) {
      return;
    }

    if (options && options.target) {
      if (options.target.type === 'line') {
        options.target.set('width', options.target.get('width') * options.target.get('scaleX'));
        options.target.set('height', options.target.get('height') * options.target.get('scaleY'));
        options.target.set('scaleX', 1);
        options.target.set('scaleY', 1);
      }
    }
  }

  onObjectMoving(e) {
    if (e.target.type === 'line') {
      const curXm = this.curX - this.getEventClientX(e.e);
      const curYm = this.curY - this.getEventClientY(e.e);

      const grab1 = e.target.handleBars.grab1;
      const grab2 = e.target.handleBars.grab2;
      grab1.set({ left: grab1.left - curXm, top: grab1.top - curYm });
      grab1.setCoords();
      grab2.set({ left: grab2.left - curXm, top: grab2.top - curYm });
      grab2.setCoords();

      if (e.target.arrowStyle === 1 && e.target.arrowTriangle) {
        //const arrowAngle = ((Math.atan2(p.x - this.sketchTarget.get('x1'), p.y - this.sketchTarget.get('y1')) * 180 / Math.PI) - 180) * -1;
        e.target.arrowTriangle.set({
          left: grab2.left,
          top: grab2.top
        });
      }
      e.target.originalX1 = grab1.get('left');
      e.target.originalY1 = grab1.get('top');
      e.target.originalX2 = grab2.get('left');
      e.target.originalY2 = grab2.get('top');

      if (this.curLine && grab1.opacity === 0) {
        this.curLine.handleBars.grab1.opacity = 0;
        this.curLine.handleBars.grab2.opacity = 0;
        this.curLine = e.target;

        grab1.opacity = 0.5;
        grab2.opacity = 0.5;
      }

      this.curX = this.getEventClientX(e.e);
      this.curY = this.getEventClientY(e.e);
      this.fabricCanvas.renderAll();
      this.onFabricChanged();
    } else if (e.target.type === 'circle') {
      const line = e.target.parent;

      e.target.parent.set('strokeWidth', e.target.parent.strokeWidth * e.target.parent.scaleX);
      //reset scale to 1
      e.target.parent.scale(1);

      if (e.target.grab1) {
        e.target.parent.set({ x1: e.target.left, y1: e.target.top });
        e.target.parent.set({ x2: line.originalX2, y2: line.originalY2 });
        e.target.parent.setCoords();
        line.originalX1 = e.target.left;
        line.originalY1 = e.target.top;

        if (line.arrowStyle === 1 && line.arrowTriangle) {
          const arrowAngle = ((Math.atan2(line.originalX2 - e.target.left, line.originalY2 - e.target.top) * 180) / Math.PI - 180) * -1;
          line.arrowTriangle.set({
            left: line.originalX2,
            top: line.originalY2,
            angle: arrowAngle
          });
        }

        this.fabricCanvas.renderAll();
        this.onFabricChanged();
      } else {
        line.set({ x1: line.originalX1, y1: line.originalY1 });
        e.target.parent.set({ x2: e.target.left, y2: e.target.top });
        e.target.parent.setCoords();

        if (line.arrowStyle === 1 && line.arrowTriangle) {
          const arrowAngle = ((Math.atan2(e.target.left - line.originalX1, e.target.top - line.originalY1) * 180) / Math.PI - 180) * -1;
          line.arrowTriangle.set({
            left: e.target.left,
            top: e.target.top,
            angle: arrowAngle
          });
        }
        this.fabricCanvas.renderAll();
        this.onFabricChanged();

        line.originalX2 = e.target.left;
        line.originalY2 = e.target.top;
      }
    }
  }

  onSelect(e) {
    if (this.ignoreSelectionEvents) {
      return;
    }
    const objType = e.target.get('type');
    if (objType === 'line') {
      this.curLine = e.target;
      this.curX = this.getEventClientX(e.e);
      this.curY = this.getEventClientY(e.e);

      if (e.target.handleBars) {
        e.target.handleBars.grab1.opacity = 0.5;
        e.target.handleBars.grab2.opacity = 0.5;
      } else {
        this.addHandlebars(e.target);
      }
    }

    this.checkAvailableOptionChanges();
  }

  onSelectUpdated(e) {
    if (this.ignoreSelectionEvents) {
      return;
    }
    if (e.target.grab1 || e.target.grab2) {
      return;
    }
    if (e.deselected && e.deselected.length > 0) {
      e.deselected.forEach((element) => {
        if (element.type === 'line' && element.handleBars) {
          this.fabricCanvas.remove(element.handleBars.grab1);
          this.fabricCanvas.remove(element.handleBars.grab2);
          delete element.handleBars;
        }
      });
    }
    if (e.target.type === 'line' && !e.target.handleBars) {
      this.addHandlebars(e.target);
    }

    this.checkAvailableOptionChanges();
  }

  onDeselect(e) {
    if (this.ignoreSelectionEvents) {
      return;
    }
    if (e.deselected && e.deselected.length > 0) {
      e.deselected.forEach((element) => {
        if (element.type === 'line' && element.handleBars) {
          this.fabricCanvas.remove(element.handleBars.grab1);
          this.fabricCanvas.remove(element.handleBars.grab2);
          delete element.handleBars;
          delete this.curLine;
        }
      });
    }
    this.checkAvailableOptionChanges();
  }

  removeHandlebars(line) {
    if (line.handleBars) {
      this.fabricCanvas.remove(line.handleBars.grab1);
      this.fabricCanvas.remove(line.handleBars.grab2);
      delete line.handleBars;
      delete this.curLine;
    }
  }

  removeAllHandlebars() {
    const objects = this.fabricCanvas.getObjects();
    objects.forEach((element) => {
      if (element.handleBars) {
        this.fabricCanvas.remove(element.handleBars.grab1);
        this.fabricCanvas.remove(element.handleBars.grab2);
        delete element.handleBars;
      }
    });
  }

  removeBackgroundImage() {
    const objects = this.fabricCanvas.getObjects();
    objects.forEach((element) => {
      if (this.isBackgroundImageObject(element)) {
        this.fabricCanvas.remove(element);
      }
    });
  }

  addHandlebars(line) {
    if (this.curLine) {
      this.removeHandlebars(this.curLine);
      delete this.curLine;
    }
    this.removeAllHandlebars();
    if (!line.handleBars) {
      this.curLine = line;

      line.handleBars = {
        grab1: new fabric.Circle({
          left: line.originalX1,
          top: line.originalY1,
          strokeWidth: 5,
          radius: 7,
          fill: '#99CCFF',
          stroke: '#99CCFF',
          originX: 'center',
          originY: 'center',
          opacity: 0.5,
          grab1: true
        }),
        grab2: new fabric.Circle({
          left: line.originalX2,
          top: line.originalY2,
          strokeWidth: 5,
          radius: 7,
          fill: '#99CCFF',
          stroke: '#99CCFF',
          originX: 'center',
          originY: 'center',
          opacity: 0.5,
          grab2: true
        })
      };

      line.handleBars.grab1.hasControls = false;
      line.handleBars.grab1.hasBorders = false;
      line.handleBars.grab2.hasControls = false;
      line.handleBars.grab2.hasBorders = false;
      line.handleBars.grab1.parent = line;
      line.handleBars.grab2.parent = line;

      this.fabricCanvas.add(line.handleBars.grab1);
      this.fabricCanvas.add(line.handleBars.grab2);
      this.fabricCanvas.renderAll();
    }
  }

  transformLegacyValue(jsonSketchValue) {
    for (const shape in jsonSketchValue.objects) {
      if (shape && jsonSketchValue.objects[shape].type === 'sketchline') {
        const scale = jsonSketchValue.objects[shape].fullScale / (1000 / this.width);
        jsonSketchValue.objects[shape].type = 'line';
        jsonSketchValue.objects[shape].version = '2.3.6';
        jsonSketchValue.objects[shape].hasControls = false;
        jsonSketchValue.objects[shape].hasBorders = false;
        jsonSketchValue.objects[shape].perPixelTargetFind = true;
        jsonSketchValue.objects[shape].groupable = false;
        jsonSketchValue.objects[shape].originX = 'center';
        jsonSketchValue.objects[shape].originY = 'center';
        jsonSketchValue.objects[shape].left =
          Math.min(jsonSketchValue.objects[shape].x1, jsonSketchValue.objects[shape].x2) +
          Math.abs(jsonSketchValue.objects[shape].x1 - jsonSketchValue.objects[shape].x2) / 2;
        jsonSketchValue.objects[shape].top =
          Math.min(jsonSketchValue.objects[shape].y1, jsonSketchValue.objects[shape].y2) +
          Math.abs(jsonSketchValue.objects[shape].y1 - jsonSketchValue.objects[shape].y2) / 2;
        jsonSketchValue.objects[shape].originalX1 = jsonSketchValue.objects[shape].x1 * scale;
        jsonSketchValue.objects[shape].originalY1 = jsonSketchValue.objects[shape].y1 * scale;
        jsonSketchValue.objects[shape].originalX2 = jsonSketchValue.objects[shape].x2 * scale;
        jsonSketchValue.objects[shape].originalY2 = jsonSketchValue.objects[shape].y2 * scale;
      }
    }

    return jsonSketchValue;
  }

  static transformNewValue(jsonSketchValue) {
    for (const shape in jsonSketchValue) {
      if (shape) {
        if (jsonSketchValue[shape].type === 'line') {
          jsonSketchValue[shape].type = 'sketchline';
          jsonSketchValue[shape].hasBorders = true;
          jsonSketchValue[shape].originX = 'left';
          jsonSketchValue[shape].originY = 'bottom';

          jsonSketchValue[shape].x1 = jsonSketchValue[shape].originalX1 / jsonSketchValue[shape].scaleX;
          jsonSketchValue[shape].y1 = jsonSketchValue[shape].originalY1 / jsonSketchValue[shape].scaleX;
          jsonSketchValue[shape].x2 = jsonSketchValue[shape].originalX2 / jsonSketchValue[shape].scaleX;
          jsonSketchValue[shape].y2 = jsonSketchValue[shape].originalY2 / jsonSketchValue[shape].scaleX;

          delete jsonSketchValue[shape].globalCompositeOperation;
          delete jsonSketchValue[shape].version;
        }
      }
    }
    return jsonSketchValue;
  }

  setValue(value) {
    if (value) {
      const jsonValue = JSON.parse(value);
      const sketchValue = jsonValue.Value;
      if (!sketchValue) {
        return;
      }

      let jsonSketchValue = JSON.parse(sketchValue);
      jsonSketchValue = this.transformLegacyValue(jsonSketchValue);

      fabric.util.enlivenObjects(jsonSketchValue.objects, (objects) => {
        for (let i = 0; i < objects.length; i++) {
          const scaleX = objects[i].fullScale / (1000 / this.width);
          const scaleY = (objects[i].fullScaleY || objects[i].fullScale) / (1000 / this.width);
          const o = objects[i];
          o.top *= scaleX;
          o.left *= scaleX;
          o.scaleX = scaleX;
          o.scaleY = scaleY;
          o.setCoords();
          o.originalScale = scaleX;

          this.restoreObject(o);
        }
      });
    }
    this.addCurrentStateToUndoStack();
  }

  restoreObject(obj) {
    if (this.isBackgroundImageObject(obj)) {
      obj.selectable = false;
      obj.hasControls = false;
      obj.hasBorders = false;
      obj.evented = false;
      obj.savable = false;
    }

    if (obj.type === 'line') {
      obj.hasControls = false;
      obj.hasBorders = false;
      obj.perPixelTargetFind = false;
      obj.groupable = false;
      obj.originX = 'center';
      obj.originY = 'center';
    }

    this.fabricCanvas.add(obj);

    if (obj.arrowStyle === 1) {
      const arrowAngle = ((Math.atan2(obj.originalX2 - obj.originalX1, obj.originalY2 - obj.originalY1) * 180) / Math.PI - 180) * -1;
      obj.arrowTriangle = new fabric.Triangle({
        selectable: false,
        fill: obj.fill,
        stroke: obj.stroke,
        angle: arrowAngle,
        left: obj.originalX2,
        top: obj.originalY2,
        width: obj.strokeWidth * this.arrowWidthFactor * obj.scaleX,
        height: obj.strokeWidth * 2.2 * obj.scaleX,
        originX: 'center',
        originY: 'center',
        discardOnSave: true
      });
      this.fabricCanvas.add(obj.arrowTriangle);
    }
  }

  setBackgroundImage(fileId, imgUrl) {
    this.imageAndFileIds[imgUrl] = fileId;

    this.clearBackgroundImage();
    // FIX: TypeMePlease
    const imageStyles = {
      type: 'image',
      selectable: false,
      hasControls: false,
      hasBorders: false,
      evented: false,
      savable: false,
      originX: 'center',
      originY: 'center',
      top: undefined,
      left: undefined
    };

    if (imgUrl) {
      fabric.Image.fromURL(imgUrl, (myImg) => {
        const canvasWidth = this.fabricCanvas.getWidth();
        const canvasHeight = this.fabricCanvas.getHeight();
        const widthRatio = myImg.width / canvasWidth;
        const heightRatio = myImg.height / canvasHeight;
        if (myImg.height > canvasHeight && myImg.width > canvasWidth) {
          if (widthRatio > heightRatio) {
            myImg.scaleToWidth(this.fabricCanvas.getWidth());
          } else {
            myImg.scaleToHeight(this.fabricCanvas.getHeight());
          }
        } else if (myImg.height < canvasHeight && myImg.width < canvasWidth) {
          if (widthRatio > heightRatio) {
            myImg.scaleToWidth(this.fabricCanvas.getWidth());
          } else {
            myImg.scaleToHeight(this.fabricCanvas.getHeight());
          }
        } else if (myImg.width > canvasWidth) {
          myImg.scaleToWidth(this.fabricCanvas.getWidth());
        } else {
          myImg.scaleToHeight(this.fabricCanvas.getHeight());
        }

        imageStyles.top = myImg._getTransformedDimensions().y / 2;
        imageStyles.left = myImg._getTransformedDimensions().x / 2;

        if (this.align === 'Center') {
          const center = this.fabricCanvas.getCenter();
          imageStyles.top = center.top;
          imageStyles.left = center.left;
        }
        const img = myImg.set(imageStyles, { crossOrigin: 'Anonymous' });

        this.fabricCanvas.add(img);
        this.keepBackgroundInBack();
      });
    }
    this.checkAvailableOptionChanges();
  }

  keepBackgroundInBack() {
    const backgroundImage = this.fabricCanvas.getObjects().find((x) => this.isBackgroundImageObject(x));
    if (backgroundImage) {
      this.fabricCanvas.sendToBack(backgroundImage);
    }
  }

  clearBackgroundImage() {
    if (this.fabricCanvas.getObjects()) {
      this.fabricCanvas.getObjects().forEach((o) => {
        if (this.isBackgroundImageObject(o)) {
          this.fabricCanvas.remove(o);
        }
      });
    }
    this.checkAvailableOptionChanges();
  }

  getValue() {
    let sketchValue = '';
    let savableObjects = [];

    let fileId = null;
    for (const obj of this.fabricCanvas.getObjects()) {
      if (this.isBackgroundImageObject(obj)) {
        const imageUrl = obj.getSrc();
        if (this.imageAndFileIds[imageUrl]) {
          fileId = this.imageAndFileIds[imageUrl];
        }
      }
    }

    //first we remove all the objects from the list that aren't savable;
    this.removeAllHandlebars();
    this.removeBackgroundImage();
    this.fabricCanvas.discardActiveObject();
    this.fabricCanvas.renderAll();

    const objects = this.fabricCanvas.getObjects();

    const canvasImage = this.getDataImage(this.originalWidth);
    if (objects && objects.length > 0) {
      for (let i = 0; i < objects.length; i++) {
        if (objects[i] && objects[i].savable !== false && objects[i].type !== 'circle' && !objects[i].discardOnSave) {
          const scaleX = objects[i].scaleX * (1000 / this.width);
          const scaleY = objects[i].scaleY * (1000 / this.width);
          objects[i].top /= objects[i].scaleX;
          objects[i].left /= objects[i].scaleX;
          if (objects[i].type === 'line') {
            objects[i].x1 /= objects[i].scaleX;
            objects[i].y1 /= objects[i].scaleX;
            objects[i].x2 /= objects[i].scaleX;
            objects[i].y2 /= objects[i].scaleX;
            if (objects[i].handleBars) {
              this.removeHandlebars(objects[i]);
            }
          } else {
            objects[i].scaleX = scaleX;
            objects[i].scaleY = scaleY;
            objects[i].setCoords();
          }
          const savableObject = objects[i].toObject(['originalX1', 'originalX2', 'originalY1', 'originalY2', 'arrowStyle']);
          savableObject.fullScale = scaleX; // we need fullScale since toObject rounds scaleX and scaleY
          savableObject.fullScaleY = scaleY;
          savableObjects.push(savableObject);
        }
      }

      savableObjects = Sketch.transformNewValue(savableObjects);
      sketchValue = JSON.stringify({ objects: savableObjects });
    }

    return { Value: sketchValue, DrawingImage: canvasImage, FileId: fileId };
  }

  setObjectScale(scale) {
    this.objectScale = scale <= 0 ? 1 : scale;
    this.fabricCanvas.freeDrawingBrush.width = scale * this.fabricCanvas.canvasScale;

    if (this.fabricCanvas.getActiveObjects()) {
      this.fabricCanvas.getActiveObjects().forEach((o) => {
        const obj = this.getRootActiveObject(o);
        if (obj.type === 'text') {
          obj.set('fontSize', 10 * this.fabricCanvas.freeDrawingBrush.width);
        } else if (obj.type === 'line' && obj.arrowTriangle) {
          obj.set('strokeWidth', this.fabricCanvas.freeDrawingBrush.width);
          obj.arrowTriangle.set('width', this.fabricCanvas.freeDrawingBrush.width * this.arrowWidthFactor);
          obj.arrowTriangle.set('height', this.fabricCanvas.freeDrawingBrush.width * 2.2);
        } else {
          obj.set('strokeWidth', this.fabricCanvas.freeDrawingBrush.width);
        }
      });
    } else if (this.fabricCanvas.getActiveObject()) {
      if (this.fabricCanvas.getActiveObject().type === 'text') {
        this.fabricCanvas.getActiveObject().set('fontSize', 10 * this.fabricCanvas.freeDrawingBrush.width);
      } else {
        this.fabricCanvas.getActiveObject().set('strokeWidth', this.fabricCanvas.freeDrawingBrush.width);
      }
    }
    this.fabricCanvas.renderAll();
    this.onFabricChanged();
  }

  getRootActiveObject(obj) {
    // if the active object is one of the circle grab handles we want to return the parent instead
    if (obj.type === 'circle' && obj.parent) {
      return obj.parent;
    }
    return obj;
  }

  getObjectScale() {
    return this.objectScale;
  }

  //color is assumed to be from #000000 to #FFFFFF
  setStrokeColor(color, update?) {
    this.selectedColor = color.substring(1);
    if (this.strokeOpacity === undefined) {
      this.strokeOpacity = 1;
    }
    if (color.length === 0) {
      this.strokeOpacity = 0;
      color = '#FFFFFF';
    } else if (this.currentMode === SketchMode.HIGHLIGHT) {
      this.strokeOpacity = 0.4;
    }

    const rgb = getRgba(color);
    this.strokeColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${this.strokeOpacity})`;
    this.fabricCanvas.freeDrawingBrush.color = this.strokeColor;

    if (update !== false) {
      if (this.fabricCanvas.getActiveObjects()) {
        this.fabricCanvas.getActiveObjects().forEach((o) => {
          const obj = this.getRootActiveObject(o);
          if (obj.type === 'text' || obj.type === 'line') {
            obj.set('fill', this.fabricCanvas.freeDrawingBrush.color);
            obj.set('stroke', this.fabricCanvas.freeDrawingBrush.color);
            if (obj.arrowTriangle) {
              obj.arrowTriangle.set('stroke', this.fabricCanvas.freeDrawingBrush.color);
              obj.arrowTriangle.set('fill', this.fabricCanvas.freeDrawingBrush.color);
            }
          } else {
            obj.set('stroke', this.fabricCanvas.freeDrawingBrush.color);
          }
        });
      } else if (this.fabricCanvas.getActiveObject()) {
        if (this.fabricCanvas.getActiveObject().type === 'text' || this.fabricCanvas.getActiveObject().type === 'line') {
          this.fabricCanvas.getActiveObject().set('fill', this.fabricCanvas.freeDrawingBrush.color);
        } else {
          this.fabricCanvas.getActiveObject().set('stroke', this.fabricCanvas.freeDrawingBrush.color);
        }
      }
    }
    this.fabricCanvas.renderAll();
    this.onFabricChanged();
  }

  getStrokeColor() {
    return `#${this.selectedColor}`;
  }

  setStrokeOpacity(opacity) {
    this.strokeOpacity = opacity;
    //TODO why do we have to set the color here? Its causing the stroke to change on selected objects whenever the mode changes
    this.setStrokeColor(this.getStrokeColor(), false);
  }

  setMode(mode) {
    const prevSketchMode = this.sketchMode;
    if (mode !== SketchMode.MOVEBEHIND && mode !== SketchMode.MOVEFRONT) {
      this.sketchMode = mode;
    }

    this.fabricCanvas.selection = mode === SketchMode.NONE;
    this.fabricCanvas.isDrawingMode = mode === SketchMode.FREEDRAW || mode === SketchMode.HIGHLIGHT;
    this.fabricCanvas.skipTargetFind = mode !== SketchMode.NONE && mode != SketchMode.FILL;

    if (mode === SketchMode.HIGHLIGHT) {
      this.setStrokeOpacity(0.4);
    } else {
      this.setStrokeOpacity(1);
    }

    if (mode === SketchMode.MOVEFRONT) {
      this.bringForward();
    }
    if (mode === SketchMode.MOVEBEHIND) {
      this.sendBackwards();
    }

    //disable sticky select for these 2 since it makes it confusing
    this.fabricCanvas.stickySelect = mode !== SketchMode.MOVEBEHIND && mode === SketchMode.MOVEFRONT;

    if (mode !== SketchMode.MOVEBEHIND && mode !== SketchMode.MOVEFRONT) {
      this.fabricCanvas.discardActiveObject().renderAll();
    }

    switch (mode) {
      case SketchMode.NONE:
        this.setCanvasPropAll('selectable', true);
        this.setCanvasPropAll('evented', true);
        this.setCanvasPropAll('perPixelTargetFind', false);
        this.fabricCanvas.hoverCursor = 'move';
        break;

      case SketchMode.FILL:
        this.setCanvasPropAll('selectable', false);
        this.setCanvasPropAll('evented', true);
        this.setCanvasPropAll('perPixelTargetFind', false);
        this.fabricCanvas.hoverCursor = 'pointer';
        break;

      default:
        if (prevSketchMode === SketchMode.NONE) {
          this.setCanvasPropAll('selectable', false);
          this.setCanvasPropAll('evented', false);
        } else if (prevSketchMode === SketchMode.FILL) {
          this.setCanvasPropAll('evented', false);
          this.setCanvasPropAll('perPixelTargetFind', true);
        }
        break;
    }
  }

  getCurrentMode() {
    return this.currentMode;
  }

  sendBackwards() {
    if (this.lastAdded) {
      this.fabricCanvas.sendBackwards(this.lastAdded);
    } else if (this.fabricCanvas.getActiveObjects()) {
      this.fabricCanvas.getActiveObjects().forEach((o) => {
        this.fabricCanvas.sendBackwards(o);
      });
    }
    this.keepBackgroundInBack();
    this.onFabricChanged();
  }

  bringForward() {
    if (this.lastAdded) {
      this.fabricCanvas.bringForward(this.lastAdded);
    } else if (this.fabricCanvas.getActiveObjects()) {
      this.fabricCanvas.getActiveObjects().forEach((o) => {
        this.fabricCanvas.bringForward(o);
      });
    }
    this.onFabricChanged();
  }

  onFabricChanged() {
    if (this.ignoreChangesForUndo) {
      return;
    }
    this.addCurrentStateToUndoStack();
  }

  addCurrentStateToUndoStack() {
    let newCurrentState = null;

    // cache and then remove the selection so that getObjects() doesn't have coords based on a group
    this.ignoreSelectionEvents = true;
    const selection = this.fabricCanvas.getActiveObjects();
    this.fabricCanvas.discardActiveObject();

    const propertiesToInclude = ['originalX1', 'originalX2', 'originalY1', 'originalY2', 'arrowStyle'];
    const fabricObjects = this.fabricCanvas
      .getObjects()
      .filter((x) => !x.discardOnSave)
      .map((x) => x.toObject(propertiesToInclude));

    if (this.curLine && this.curLine.handleBars) {
      // If there is a line currently selected, there are two additional circles on the canvas that we need to make sure not to save
      const objects = fabricObjects.map((x) => JSON.stringify(x));
      if (this.curLine && this.curLine.handleBars) {
        const grab1Index = objects.indexOf(JSON.stringify(this.curLine.handleBars.grab1));
        if (grab1Index > -1) {
          objects.splice(grab1Index, 1);
        }
        const grab2Index = objects.indexOf(JSON.stringify(this.curLine.handleBars.grab2));
        if (grab2Index > -1) {
          objects.splice(grab2Index, 1);
        }
      }
      newCurrentState = JSON.stringify(objects.map((x) => JSON.parse(x)));
    } else {
      newCurrentState = JSON.stringify(fabricObjects);
    }

    // reset the selection if there was one
    if (selection.length > 1) {
      this.fabricCanvas.setActiveObject(
        new fabric.ActiveSelection(selection, {
          canvas: this.fabricCanvas
        })
      );
    } else if (selection.length === 1) {
      this.fabricCanvas.setActiveObject(selection[0]);
    }
    this.ignoreSelectionEvents = false;

    if (this.currentState === null) {
      this.currentState = newCurrentState;
    }
    if (this.currentState !== newCurrentState) {
      this.undoStack.push(this.currentState);
      this.currentState = newCurrentState;
      this.redoStack.splice(0);
      if (this.undoStack.length > this.undoStackLength) {
        this.undoStack.splice(0, 1);
      }
    }

    this.checkAvailableOptionChanges();
  }

  undo() {
    if (this.undoStack.length > 0) {
      this.redoStack.push(this.currentState);
      this.currentState = this.undoStack.pop();

      this.restoreToState(this.currentState);

      this.ignoreChangesForUndo = false;
      this.checkAvailableOptionChanges();
    }
  }

  redo() {
    if (this.redoStack.length > 0) {
      this.undoStack.push(this.currentState);
      this.currentState = this.redoStack.pop();
      this.restoreToState(this.currentState);

      this.checkAvailableOptionChanges();
    }
  }

  restoreToState(state) {
    const fabricObjects = JSON.parse(state);

    this.ignoreChangesForUndo = true;
    this.fabricCanvas.clear();
    fabric.util.enlivenObjects(fabricObjects, (objects) => {
      objects.forEach((obj) => {
        this.restoreObject(obj);
      });
    });
    this.fabricCanvas.requestRenderAll();

    this.ignoreChangesForUndo = false;
  }

  getAvailableOptions() {
    const activeObjects = this.fabricCanvas.getActiveObjects();
    return {
      undoAvailable: this.undoStack.length > 0,
      redoAvailable: this.redoStack.length > 0,
      containsImage: this.fabricCanvas.getObjects().some((x) => this.isBackgroundImageObject(x)),
      objectSelected: activeObjects.length > 0,
      textSelected: activeObjects.length === 1 && activeObjects[0].type === 'text'
    };
  }

  checkAvailableOptionChanges() {
    const newAvailableOptions = this.getAvailableOptions();
    const availableOptionTypes: AvailableOptionTypes[] = ['undoAvailable', 'redoAvailable', 'containsImage', 'objectSelected', 'textSelected'];
    for (const ao of availableOptionTypes) {
      if (!this.lastAvailableOptions || this.lastAvailableOptions[ao] !== newAvailableOptions[ao]) {
        this.onAvailableOptionsChanged(ao, newAvailableOptions[ao]);
      }
    }

    this.lastAvailableOptions = newAvailableOptions;
  }
}
